import React, { Suspense, useContext, useEffect, useCallback, useState } from 'react';
import { Route, Routes, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { getUser, authSelector } from './store/auth/slice';
import * as Storage from './utilities/storage';
import { CURRENT_USER } from './constants/storage';
import Nav from './components/Nav/Nav';
import AdminNav from './components/Nav/adminNav';
import OrgNav from './components/Nav/orgNav';
import Header from './components/Nav/Header';
import { publicRoutes, routesByRole } from './routes';
import { ThemeContext } from './context/ThemeContext';
import { isUserLogedIn } from './utilities/auth';
import TitleChanger from './TitleChanger';
import SyncNotifications from './components/PageHeader/notifications';
import Users from './pages/Settings/users';
import Settings from './pages/Settings';
import Contact from './pages/Settings/addressBook/contacts';
import Contract from './pages/Settings/addressBook/contracts';
import Network from './pages/Settings/addressBook/network';
import Profile from './pages/Settings/addressBook/profile';
import Password from './pages/Settings/addressBook/password';
import MetaMask from './pages/Settings/addressBook/metamask';
import { PersistGate } from 'redux-persist/integration/react';
import store, { persistor } from './store';

const App = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const isUserAuthenticated = isUserLogedIn();
  const { isDarkTheme } = useContext(ThemeContext);
  const { user } = useSelector(authSelector);
  const [notificationClass, setNotificationClass] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    if (isDarkTheme) {
      document.body.classList.add('dark-body');
    } else {
      document.body.classList.remove('dark-body');
    }
  }, [isDarkTheme]);

  const getAuthUserDetailFromHFMServer = useCallback(() => {
    const storedUser = Storage.get(CURRENT_USER);
    if (isUserAuthenticated && storedUser.email) dispatch(getUser(storedUser));
  }, [isUserAuthenticated, dispatch]);

  useEffect(() => getAuthUserDetailFromHFMServer(), [getAuthUserDetailFromHFMServer]);

  useEffect(() => {
    if (user?.role === 'super_admin' && location.pathname === '/') {
      navigate('/admin/hedge-funds');
    }

    if(user.role === 'manager'  && location.pathname === '/') {
      navigate('/home');
    }

  });

  useEffect(() => {
    if (!isUserAuthenticated) {
      navigate('/login');
    }
  }, [isUserAuthenticated]);

  /**
   * Function to get role and return appropriate nav bar.
   * @param {*} role
   * @returns navbar
   */
  const getNavBar = (user) => {
    if (
      location.pathname === '/login' ||
      location.pathname === '/forgot-password' ||
      location.pathname === '/privacy-and-policy' ||
      location.pathname === '/terms-and-conditions'
    )
      return;
    if (!user.account_type) return;
    return (
      (user.account_type === 'super_admin' && <AdminNav />) ||
      (user.account_type === 'standard' && <Nav />) ||
      (user.account_type === 'organization' && <OrgNav role={user.role} />)
    );
  };

  const NotFound = () => {
    return <Navigate to="/" />;
  };

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <TitleChanger />
        <div className={`app ${isDarkTheme === true ? 'dark' : undefined}`}>
          <Suspense fallback={'loading...'}>
            <SyncNotifications setNotificationClass={setNotificationClass} />
            <div className={notificationClass}>
              {location.pathname !== '/login' &&
                location.pathname !== '/forgot-password' &&
                location.pathname !== '/privacy-and-policy' &&
                location.pathname !== '/terms-and-conditions' && <Header />}
              {/* {location.pathname !== '/login' && user.role !== 'super_admin' && <Nav />} */}
              {/* {location.pathname !== '/login' && user.role === 'super_admin' && <AdminNav />} */}
              {getNavBar(user)}
              <Routes>
                {/* <Route index element={NotFound} /> */}
                {user.account_type &&
                  routesByRole[user.account_type].map((route, id) => (
                    <Route
                      path={route.path}
                      element={<PrivateRoute>{route.element}</PrivateRoute>}
                      key={id + route.path}
                    />
                  ))}
                {
                  <Route path="/settings" element={<Settings />}>
                    <Route
                      index
                      element={
                        <PrivateRoute>
                          {user.account_type === 'super_admin' ? Users : Contact}
                        </PrivateRoute>
                      }
                      key={'user'}
                    />
                    {user.account_type !== 'super_admin' && (
                      <Route
                        path="contracts"
                        element={<PrivateRoute>{Contract}</PrivateRoute>}
                        key={'user'}
                      />
                    )}
                    {user.account_type !== 'super_admin' && (
                      <Route
                        path="network"
                        element={<PrivateRoute>{Network}</PrivateRoute>}
                        key={'user'}
                      />
                    )}
                    {user.account_type !== 'super_admin' && (
                      <Route
                        path="profile"
                        element={<PrivateRoute>{Profile}</PrivateRoute>}
                        key={'user'}
                      />
                    )}
                    {
                      <Route
                        path="change-password"
                        element={<PrivateRoute>{Password}</PrivateRoute>}
                        key={'user'}
                      />
                    }
                    {
                      <Route
                        path="metamask"
                        element={<PrivateRoute>{MetaMask}</PrivateRoute>}
                        key={'user'}
                      />
                    }
                  </Route>
                }
                {publicRoutes.map((route, id) => (
                  <Route path={route.path} element={<route.element />} key={id} />
                ))}
              </Routes>
            </div>
          </Suspense>
        </div>
      </PersistGate>
    </Provider>
  );
};

export const PrivateRoute = ({ children: Component }) => {
  const isUserAuthenticated = isUserLogedIn();
  const location = useLocation();

  return isUserAuthenticated ? <Component /> : <Navigate to="/login" state={{ from: location }} />;
};

export default App;
